//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Moment } from 'moment-timezone';

import { isTwitterMediaPostConfig } from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import {
  searchScheduledPosts,
  deleteScheduledPosts,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import {
  IScheduledTweet,
  ISearchScheduledPostsRequest,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  UniversalPostType,
  UniversalPostingGroupName,
} from '@apis/universal-posts/universalPostsApiModel';
import DateString from '@models/DateString';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { getCurrentStartDate } from '@twitter/redux/schedule/selectors/getCurrentTwitterStartDate';
import {
  ITwitterScheduleState,
  NormalizedScheduledTweets,
  SCHEDULE_TWEET_FETCH_LIMIT,
} from '@twitter/redux/schedule/twitterScheduleModel';
import {
  getTweetFetchStartDate,
  mergeScheduledTweetLists,
} from '@twitter/redux/schedule/twitterScheduleUtils';
import { Normalized } from '@utils/normalize';
import { RequestError } from '@utils/sessionFetch';

const initialState: ITwitterScheduleState = {
  scheduledTweets: Normalized.empty(),
  startDate: DateString.getToday(TimeZoneId.Utc),

  errors: {},
  flags: {
    isFetchingScheduledTweets: false,
    isInitialScheduleDataLoaded: false,
    isDeletingTweet: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterSchedule,
  initialState,
  reducers: {
    setInitialScheduledData(
      state,
      action: PayloadAction<{
        scheduledTweets: NormalizedScheduledTweets;
        startDate: DateString;
        fetchScheduledTweetsError?: string;
      }>,
    ) {
      state.flags.isInitialScheduleDataLoaded = true;
      state.scheduledTweets = action.payload.scheduledTweets;
      state.startDate = action.payload.startDate;
      state.errors.fetchScheduledTweetsError =
        action.payload.fetchScheduledTweetsError;
    },
    setStartDate(state, action: PayloadAction<DateString>) {
      state.startDate = action.payload;
    },
    requestScheduledPosts(state) {
      state.errors.fetchScheduledTweetsError = undefined;
      state.flags.isFetchingScheduledTweets = true;
    },
    receiveScheduledPostsSucceeded(
      state,
      action: PayloadAction<NormalizedScheduledTweets>,
    ) {
      state.flags.isFetchingScheduledTweets = false;
      state.scheduledTweets = action.payload;
    },
    receiveScheduledPostsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingScheduledTweets = false;
      state.errors.fetchScheduledTweetsError = action.payload;
    },
    requestDeleteScheduledTweet(state) {
      state.flags.isDeletingTweet = true;
    },
    deleteScheduledTweetSucceeded(state) {
      state.flags.isDeletingTweet = false;
    },
    deleteScheduledTweetFailed(state) {
      state.flags.isDeletingTweet = false;
    },
  },
});

export const loadInitialScheduleData = createThunk(
  () => async (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);

    try {
      const scheduledTweets = await dispatch(
        getAndParseAllScheduledTweets(today),
      );
      dispatch(
        actions.setInitialScheduledData({ scheduledTweets, startDate: today }),
      );
    } catch (e) {
      const fetchScheduledTweetsError = (e as RequestError).message;
      dispatch(
        actions.setInitialScheduledData({
          scheduledTweets: Normalized.empty(),
          startDate: today,
          fetchScheduledTweetsError,
        }),
      );
    }
  },
);

export const refreshScheduleData = createThunk(
  () => async (dispatch, getState) => {
    const { isFetchingScheduledTweets } = getState().twitter.schedule.flags;
    const startDate = getCurrentStartDate(getState());

    if (isFetchingScheduledTweets) {
      return;
    }

    dispatch(actions.requestScheduledPosts());

    try {
      const scheduledPosts = await dispatch(
        getAndParseAllScheduledTweets(startDate),
      );
      dispatch(actions.receiveScheduledPostsSucceeded(scheduledPosts));
    } catch (e) {
      const err = e as RequestError;
      dispatch(actions.receiveScheduledPostsFailed(err.message));
    }
  },
);

export const setStartDateAndFetchPosts = createThunk(
  (newStartDate: DateString) => async (dispatch, getState) => {
    const currentStartDate = getCurrentStartDate(getState());
    dispatch(actions.setStartDate(newStartDate));

    const isNewDateFartherInPast =
      DateString.getDayOffset(newStartDate, currentStartDate) > 0;
    if (isNewDateFartherInPast) {
      dispatch(actions.requestScheduledPosts());

      try {
        const currentTweets = getState().twitter.schedule.scheduledTweets;
        const newTweets = await dispatch(
          getAndParseAllScheduledTweets(newStartDate, currentStartDate),
        );
        const combinedTweets = mergeScheduledTweetLists([
          currentTweets,
          newTweets,
        ]);
        dispatch(actions.receiveScheduledPostsSucceeded(combinedTweets));
      } catch (e) {
        const err = e as RequestError;
        dispatch(actions.receiveScheduledPostsFailed(err.message));
      }
    }
  },
);

const getAndParseScheduledTweetSet = async (
  start: Moment,
  end?: Moment,
  offset = 0,
) => {
  const requestParams = {};

  const searchRequest: ISearchScheduledPostsRequest = {
    after: start.toDate(),
    before: end?.toDate(),
    limit: SCHEDULE_TWEET_FETCH_LIMIT,
    offset,
    status: [
      PostableStatus.Dispatched,
      PostableStatus.Failed,
      PostableStatus.Pending,
      PostableStatus.Posted,
      PostableStatus.Processing,
      PostableStatus.Scheduled,
    ],
    groups: [UniversalPostingGroupName.TwitterPostingGroup],
  };

  const { data: scheduledPosts } = await searchScheduledPosts(searchRequest);
  const scheduledTweets = scheduledPosts.map((scheduledPost) => {
    if (scheduledPost.postType === UniversalPostType.TwitterMediaPost) {
      return scheduledPost as IScheduledTweet;
    } else {
      throw new Error(
        `Retrieved invalid post config type: ${scheduledPost.config['@type']}`,
      );
    }
  });

  const isEndReached = scheduledTweets.length < SCHEDULE_TWEET_FETCH_LIMIT;

  return {
    isEndReached,
    scheduledTweets,
  };
};

const getAndParseAllScheduledTweets = createThunk(
  (startDate: DateString, endDate?: DateString) =>
    async (dispatch, getState) => {
      const tz = getUserTimeZone(getState());
      const start = DateString.toMoment(getTweetFetchStartDate(startDate), tz);
      const end = endDate && DateString.toMoment(endDate, tz);
      let { isEndReached, scheduledTweets } =
        await getAndParseScheduledTweetSet(start, end);

      while (!isEndReached) {
        const nextResponse = await getAndParseScheduledTweetSet(
          start,
          end,
          scheduledTweets.length,
        );
        isEndReached = nextResponse.isEndReached;

        scheduledTweets = [...scheduledTweets, ...nextResponse.scheduledTweets];
      }

      return Normalized.from(scheduledTweets, (tweet) => tweet.id);
    },
);

export const deleteScheduledTweet = createThunk(
  (postId: PostableId) =>
    async (dispatch, getState): Promise<boolean> => {
      dispatch(actions.requestDeleteScheduledTweet());

      try {
        await deleteScheduledPosts([postId]);

        dispatch(actions.deleteScheduledTweetSucceeded());

        return true;
      } catch (e) {
        dispatch(actions.deleteScheduledTweetFailed());
        return false;
      }
    },
);

const changeStartDateByOffset = createThunk(
  (offset: number) => (dispatch, getState) => {
    const currentStartDate = getCurrentStartDate(getState());
    const newStartDate = DateString.addDays(currentStartDate, offset);
    dispatch(setStartDateAndFetchPosts(newStartDate));
  },
);

export const navigateCalendarForward = createThunk(() => (dispatch) => {
  dispatch(changeStartDateByOffset(1));
});

export const navigateCalendarBackward = createThunk(() => (dispatch) => {
  dispatch(changeStartDateByOffset(-1));
});

export const navigateCalendarToDate = createThunk(
  (date: DateString) => (dispatch) => {
    dispatch(setStartDateAndFetchPosts(date));
  },
);

export const navigateCalendarToToday = createThunk(
  () => (dispatch, getState) => {
    const tz = getUserTimeZone(getState());
    const today = DateString.getToday(tz);
    dispatch(navigateCalendarToDate(today));
  },
);

export default reducer;
